<template>
  <AuthLayout>
    <div class="tw:flex tw:flex-col tw:gap-1.5" data-testid="sso-link">
      <h1 class="tw:m-0! tw:text-30! tw:font-bold! tw:tracking-[-0.02em]! tw:text-gray-900!">
        {{ $t('sso.link.title') }}
      </h1>
      <p class="tw:m-0 tw:text-14 tw:text-gray-600 tw:[text-wrap:pretty]">
        <template v-if="email">
          {{ $t('sso.link.lede_before', { provider: providerName }) }}
          <strong class="tw:font-semibold tw:text-gray-900" data-testid="sso-link-address">{{ email }}</strong>
          {{ $t('sso.link.lede_after', { minutes: expiryMinutes }) }}
        </template>
        <template v-else>{{ $t('sso.link.lede_unknown_address') }}</template>
      </p>
    </div>

    <form class="tw:flex tw:flex-col tw:gap-6" novalidate @submit.prevent="submit">
      <div class="tw:flex tw:flex-col tw:gap-3">
        <CodeInput
          ref="codeRef"
          v-model="code"
          :has-error="Boolean(error)"
          :disabled="isSubmitting"
          :aria-label="$t('sso.link.code_label')"
          data-testid="sso-link-code"
          @complete="submit">
          <template #aside>
            <ResendCountdown ref="countdownRef" :seconds="resendAfterSeconds" :busy="isResending" @resend="resend" />
          </template>
        </CodeInput>

        <span v-if="error" class="tw:text-13 tw:text-danger" role="alert" data-testid="sso-link-error">{{
          error
        }}</span>
      </div>

      <div class="tw:flex tw:flex-col tw:gap-4">
        <LeadButton
          type="submit"
          block
          :loading="isSubmitting"
          :disabled="code.length !== CODE_LENGTH"
          data-testid="sso-link-submit">
          {{ $t('sso.link.confirm') }}
        </LeadButton>
        <p class="tw:m-0 tw:text-center tw:text-14 tw:text-gray-600">
          <RouterLink :to="{ name: 'login' }" class="tw:auth-link" data-testid="sso-link-cancel">
            {{ $t('auth.back_to_sign_in') }}
          </RouterLink>
        </p>
      </div>
    </form>
  </AuthLayout>
</template>

<script setup lang="ts">
/**
 * After a Google or Microsoft sign-in whose email matches an account that has no link to it yet: the code sent to
 * that address proves the person owns the account before the identity is linked and the session signed in
 */
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue';
import { useRouter } from 'vue-router';

import type { ClientError } from '@/api/client';
import { confirmSSOLink, resendSSOLinkCode } from '@/api/sso';
import AuthLayout from '@/components/auth/AuthLayout.vue';
import LeadButton from '@/components/lead-design/LeadButton.vue';
import CodeInput from '@/components/registration/subcomponents/CodeInput.vue';
import ResendCountdown from '@/components/registration/subcomponents/ResendCountdown.vue';
import {
  registrationErrorMessage,
  verificationErrorMessage,
} from '@/components/registration/subcomponents/registrationErrors';
import { useSignedInLanding } from '@/composables/useSignedInLanding';
import { isSSOProvider, SSO_PROVIDERS } from '@/composables/useSSOProviders';

const CODE_LENGTH = 6;

/* ----------------------------------- STATE ---------------------------------- */
const router = useRouter();
const land = useSignedInLanding();

/** handed over by the callback page; gone after a reload, when the page still works without the address */
const handedOver = (window.history.state ?? {}) as Record<string, unknown>;

const email = typeof handedOver.email === 'string' ? handedOver.email : '';
const expiresInSeconds = ref(typeof handedOver.expiresInSeconds === 'number' ? handedOver.expiresInSeconds : 0);
const resendAfterSeconds = ref(typeof handedOver.resendAfterSeconds === 'number' ? handedOver.resendAfterSeconds : 0);

const code = ref('');
const error = ref('');
const isSubmitting = ref(false);
const isResending = ref(false);

const codeRef = useTemplateRef<InstanceType<typeof CodeInput>>('codeRef');
const countdownRef = useTemplateRef<InstanceType<typeof ResendCountdown>>('countdownRef');

/* --------------------------------- COMPUTED --------------------------------- */
const providerName = computed(() =>
  isSSOProvider(handedOver.provider) ? SSO_PROVIDERS[handedOver.provider].name : 'Google / Microsoft'
);

/** stated in minutes because that is how the sentence reads */
const expiryMinutes = computed(() => Math.max(1, Math.round(expiresInSeconds.value / 60)));

/* --------------------------------- METHODS ---------------------------------- */
/** an SSO failure means the pending link is gone, so the sign-in has to start over from the login page */
function handleError(caught: unknown) {
  const errorCode = (caught as ClientError)?.responseJson?.code;

  if (typeof errorCode === 'string' && errorCode.startsWith('sso_')) {
    router.replace({ name: 'login', query: { sso_error: errorCode } });
    return;
  }

  error.value = registrationErrorMessage(caught, (code) => verificationErrorMessage(code, 'email'));
}

async function submit() {
  if (isSubmitting.value || code.value.length !== CODE_LENGTH) return;

  isSubmitting.value = true;
  error.value = '';

  try {
    const result = await confirmSSOLink(code.value);
    await land(result.returnPath);
  } catch (caught) {
    handleError(caught);
    code.value = '';
  } finally {
    isSubmitting.value = false;
  }
}

async function resend() {
  if (isResending.value) return;

  isResending.value = true;
  error.value = '';

  try {
    const response = await resendSSOLinkCode();
    expiresInSeconds.value = response.expiresInSeconds;
    resendAfterSeconds.value = response.resendAfterSeconds;
    // restart explicitly: a repeat resend often returns the same cooldown, so the watcher wouldn't fire
    countdownRef.value?.restart(response.resendAfterSeconds);
    code.value = '';
  } catch (caught) {
    handleError(caught);
  } finally {
    isResending.value = false;
  }
}

/* -------------------------------- LIFECYCLE --------------------------------- */
onMounted(() => {
  // the code field is the only thing to do, so focus it
  nextTick(() => codeRef.value?.focusFirst());
});

/* --------------------------------- WATCHERS --------------------------------- */
watch(code, (value) => {
  // clear as soon as the user starts correcting
  if (error.value && value.length > 0) error.value = '';
});
</script>
